import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui';
import { PlatformCheckoutBrand } from '../components/auth/PlatformCheckoutBrand';
import { PlatformCheckoutShell } from '../components/auth/PlatformCheckoutShell';
import { CheckCircle, Mail, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { applyActionCode, reload } from 'firebase/auth';
import { auth } from '../config/firebase';
import { AuthService } from '../services/auth';
import { useAuthStore } from '../store/auth';
import { academyService } from '../services/academy';
import type { Academy } from '@court-sync/shared';
import { buildPlatformLoginHref, buildPostVerificationLoginTarget, buildResendVerificationReturnUrl, extractAcademySubdomainLabelFromReturnUrl, isAuthenticatedPostVerificationRoute, parseReturnUrl, resolveEffectiveAcademySlug, resolveEmailVerificationAcademySlug, resolveUnsignedPostVerificationRedirect } from '../utils/emailVerificationRedirect';

export default function EmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'pending' | 'success' | 'error'>('pending');
  const [errorMessage, setErrorMessage] = useState('');
  const [academy, setAcademy] = useState<Academy | null>(null);
  const verificationAttemptedRef = useRef(false);

  const email = searchParams.get('email');
  const token = searchParams.get('token');
  const oobCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');
  const academySlugParam = searchParams.get('academySlug');
  const academySubdomainParam = searchParams.get('academySubdomain');

  const verificationEmailSendFailed = Boolean(
    (location.state as { verificationEmailSendFailed?: boolean } | null)?.verificationEmailSendFailed
  );

  const [returnUrl] = useState(
    () => searchParams.get('returnUrl') || (location.state as { returnUrl?: string } | null)?.returnUrl
  );

  useEffect(() => {
    if (searchParams.get('returnUrl')) {
      const params = new URLSearchParams(searchParams);
      params.delete('returnUrl');
      const cleanSearch = params.toString();
      navigate(`${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });
    }
  }, [location.pathname, navigate, searchParams]);

  const academySlugFromParams = resolveEmailVerificationAcademySlug({
    academySlugParam,
    returnUrl,
    currentOrigin: typeof window !== 'undefined' ? window.location.origin : '',
  });
  const subdomainLabel =
    academySubdomainParam ??
    (returnUrl ? extractAcademySubdomainLabelFromReturnUrl(returnUrl) : null);

  useEffect(() => {
    let cancelled = false;

    const loadAcademy = async () => {
      if (academySlugFromParams) {
        try {
          const loaded = await academyService.getAcademyBySlug(academySlugFromParams);
          if (!cancelled) setAcademy(loaded);
        } catch {
          if (!cancelled) setAcademy(null);
        }
        return;
      }

      if (subdomainLabel) {
        try {
          const loaded = await academyService.getAcademyBySubdomain(subdomainLabel);
          if (!cancelled) setAcademy(loaded);
        } catch {
          if (!cancelled) setAcademy(null);
        }
        return;
      }

      if (!cancelled) setAcademy(null);
    };

    void loadAcademy();
    return () => {
      cancelled = true;
    };
  }, [academySlugFromParams, subdomainLabel]);

  const resolvedAcademySlug = academy?.slug ?? academySlugFromParams;

  const getEffectiveAcademySlug = useCallback(
    () =>
      resolveEffectiveAcademySlug({
        resolvedAcademySlug,
        academySlugParam,
        academySubdomainParam,
        subdomainLabel,
      }),
    [academySlugParam, academySubdomainParam, resolvedAcademySlug, subdomainLabel]
  );

  const applyPostVerificationRedirect = useCallback(
    (redirect: { type: 'external'; href: string } | { type: 'navigate'; href: string; state?: { email?: string; message?: string } }) => {
      if (redirect.type === 'external') {
        window.location.assign(redirect.href);
        return;
      }
      navigate(redirect.href, { state: redirect.state });
    },
    [navigate]
  );

  const navigateToLogin = useCallback(
    (message: string) => {
      const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      const effectiveSlug = getEffectiveAcademySlug();

      if (returnUrl) {
        const parsed = parseReturnUrl(returnUrl, currentOrigin);
        const unsignedRedirect = resolveUnsignedPostVerificationRedirect({
          parsed,
          effectiveAcademySlug: effectiveSlug,
          academy,
          email,
          loginMessage: message,
        });
        if (unsignedRedirect) {
          applyPostVerificationRedirect(unsignedRedirect);
          return;
        }
      }

      if (effectiveSlug) {
        const loginTarget = buildPostVerificationLoginTarget({
          academy,
          academySlug: effectiveSlug,
          returnPath: '/dashboard',
          email,
          message,
        });
        if (loginTarget.external) {
          window.location.assign(loginTarget.href);
          return;
        }
        navigate(loginTarget.href, {
          state: { email: email ?? undefined, message },
        });
        return;
      }

      const returnPath = returnUrl
        ? parseReturnUrl(returnUrl, currentOrigin).path
        : undefined;
      navigate(buildPlatformLoginHref(returnPath, email, message), {
        state: { email: email ?? undefined, message },
      });
    },
    [academy, applyPostVerificationRedirect, email, getEffectiveAcademySlug, navigate, returnUrl]
  );

  const handleRedirect = useCallback(() => {
    const signedIn = Boolean(auth.currentUser?.emailVerified);
    const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const effectiveSlug = getEffectiveAcademySlug();

    if (returnUrl) {
      const parsed = parseReturnUrl(returnUrl, currentOrigin);
      const returnPath = parsed.path;
      const successState = {
        email: email ?? undefined,
        message: 'Email verified successfully! Your account is now fully set up.',
        showOnboarding: true,
      };

      if (isAuthenticatedPostVerificationRoute(returnPath) && signedIn) {
        if (parsed.isCrossOrigin) {
          window.location.assign(parsed.href);
        } else {
          navigate(returnPath, { state: successState });
        }
        return;
      }

      const loginMessage = 'Email verified successfully! Please sign in to continue.';
      const unsignedRedirect = resolveUnsignedPostVerificationRedirect({
        parsed,
        effectiveAcademySlug: effectiveSlug,
        academy,
        email,
        loginMessage,
      });
      if (unsignedRedirect) {
        applyPostVerificationRedirect(unsignedRedirect);
        return;
      }

      navigate(buildPlatformLoginHref(returnPath, email, loginMessage), {
        state: {
          email: email ?? undefined,
          message: loginMessage,
        },
      });
      return;
    }

    navigate('/dashboard', {
      state: {
        email: email ?? undefined,
        message: 'Email verified successfully! Your account is now fully set up.',
      },
    });
  }, [academy, applyPostVerificationRedirect, email, getEffectiveAcademySlug, location.state, navigate, returnUrl]);

  const handleEmailVerification = useCallback(
    async (verificationToken: string) => {
      setIsVerifying(true);
      setErrorMessage('');

      try {
        await applyActionCode(auth, verificationToken);
        if (auth.currentUser) {
          await reload(auth.currentUser);
          await auth.currentUser.getIdToken(true);
        }

        await useAuthStore.getState().refreshUser({ forceTokenRefresh: true });

        setVerificationStatus('success');
        toast.success('Email verified successfully!');

        const params = new URLSearchParams(window.location.search);
        params.delete('oobCode');
        params.delete('mode');
        params.delete('apiKey');
        params.delete('lang');
        const cleanSearch = params.toString();
        navigate(`${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`, { replace: true });

        setTimeout(() => {
          handleRedirect();
        }, 2000);
      } catch (error: unknown) {
        const code = (error as { code?: string })?.code;
        setVerificationStatus('error');
        const message =
          code === 'auth/invalid-action-code'
            ? 'The verification link is invalid or has expired.'
            : code === 'auth/expired-action-code'
              ? 'The verification link has expired. Please request a new one.'
              : 'Verification failed. Please try again.';
        setErrorMessage(message);
        toast.error(message);
        console.error('Email verification error:', error);
      } finally {
        setIsVerifying(false);
      }
    },
    [handleRedirect, location.pathname, navigate]
  );

  useEffect(() => {
    if (verificationAttemptedRef.current) {
      return;
    }

    if (oobCode && mode === 'verifyEmail') {
      verificationAttemptedRef.current = true;
      void handleEmailVerification(oobCode);
    } else if (token) {
      verificationAttemptedRef.current = true;
      void handleEmailVerification(token);
    }
  }, [oobCode, mode, token, handleEmailVerification]);

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Email address not found');
      return;
    }

    setIsResending(true);

    try {
      const verificationReturnUrl = buildResendVerificationReturnUrl(
        returnUrl,
        `${location.pathname}${location.search}`,
        window.location.origin
      );
      await AuthService.resendEmailVerification(email, { returnUrl: verificationReturnUrl });

      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to resend verification email';
      toast.error(message);
      console.error('Resend verification error:', error);
    } finally {
      setIsResending(false);
    }
  };

  const handleGoToLogin = () => {
    navigateToLogin('Please verify your email before logging in.');
  };

  const pageTitle = academy?.name ? `Verify your email` : 'Verify your email';

  const verificationLeft = (
    <>
      {academy?.logoUrl ? (
        <div className="mb-8">
          <img
            src={academy.logoUrl}
            alt={academy.name}
            className="h-14 w-auto object-contain"
            referrerPolicy="no-referrer"
          />
          <p className="mt-3 text-sm font-medium text-gray-900 dark:text-white">{academy.name}</p>
        </div>
      ) : (
        <PlatformCheckoutBrand className="mb-4 md:mb-8" />
      )}
      <h1 className="mb-2 text-xl font-bold text-gray-900 dark:text-white sm:mb-3 sm:text-2xl md:text-3xl">
        {pageTitle}
      </h1>
      <p className="mb-4 text-sm text-gray-600 dark:text-gray-400 sm:mb-6 sm:text-base">
        {verificationStatus === 'success'
          ? 'Your account is verified. We’ll take you to the next step in a moment.'
          : verificationStatus === 'error'
            ? 'The link may have expired. Request a new verification email to continue.'
            : 'We sent a secure link to your inbox. Open it to activate your account and continue.'}
      </p>
      <ul className="hidden space-y-3 text-sm text-gray-600 dark:text-gray-400 sm:block">
        <li className="flex gap-2">
          <span className="shrink-0 font-semibold text-green-600 dark:text-green-400">✓</span>
          <span>Link expires in 24 hours for your security</span>
        </li>
        <li className="flex gap-2">
          <span className="shrink-0 font-semibold text-green-600 dark:text-green-400">✓</span>
          <span>Check spam or junk if you don&apos;t see it</span>
        </li>
        <li className="flex gap-2">
          <span className="shrink-0 font-semibold text-green-600 dark:text-green-400">✓</span>
          <span>Resend anytime from this page</span>
        </li>
      </ul>
      {email && (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 sm:mt-8">
          Sent to{' '}
          <span className="font-medium text-gray-900 dark:text-white">{email}</span>
        </p>
      )}
      {!academy && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-500 sm:mt-8">
          Already verified?{' '}
          <Link to="/login" className="font-medium text-primary-600 hover:underline dark:text-primary-400">
            Sign in
          </Link>
        </p>
      )}
    </>
  );

  const mainHeading =
    verificationStatus === 'success'
      ? 'Email verified'
      : verificationStatus === 'error'
        ? 'Verification failed'
        : isVerifying
          ? 'Verifying…'
          : 'Check your email';

  const mainSubheading =
    verificationStatus === 'success'
      ? 'Redirecting you to continue'
      : verificationStatus === 'error'
        ? 'Try resending the verification email'
        : isVerifying
          ? 'Please wait while we confirm your address'
          : 'Open the link we sent to your inbox';

  return (
    <PlatformCheckoutShell asideVariant="onboarding" left={verificationLeft}>
      <div className="mx-auto w-full max-w-md lg:mx-0 lg:ml-10 lg:max-w-lg">
        <div className="mb-6 space-y-1">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{mainHeading}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">{mainSubheading}</p>
        </div>

        <div className="space-y-6">
          {verificationEmailSendFailed && verificationStatus === 'pending' && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
              <p className="text-sm text-amber-900 dark:text-amber-200">
                Your account was created, but we could not send the verification email. Use{' '}
                <strong>Resend Verification Email</strong> below or check that your email address is correct.
              </p>
            </div>
          )}

          {verificationStatus === 'success' && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-6 dark:border-green-800 dark:bg-green-900/20">
              <div className="flex flex-col items-center text-center">
                <CheckCircle className="mb-4 h-12 w-12 text-green-600 dark:text-green-400" />
                <p className="text-base font-medium text-green-900 dark:text-green-200">
                  Your email has been verified successfully!
                </p>
                <p className="mt-2 text-sm text-green-800 dark:text-green-300">
                  Redirecting you to continue…
                </p>
                <Loader2 className="mt-4 h-5 w-5 animate-spin text-primary-600" />
              </div>
            </div>
          )}

          {verificationStatus === 'error' && (
            <div className="space-y-6">
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                  <p className="text-sm font-medium text-red-800 dark:text-red-200">
                    {errorMessage || 'The verification link is invalid or has expired.'}
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <Button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="w-full btn-primary"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>
                <Button variant="outline" onClick={handleGoToLogin} className="w-full">
                  Go to Login
                </Button>
              </div>
            </div>
          )}

          {verificationStatus === 'pending' && !token && !oobCode && (
            <div className="space-y-6">
              <div className="rounded-lg border border-border bg-muted/30 p-6 dark:bg-muted/20">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
                    <Mail className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-900 dark:text-white">
                      We&apos;ve sent a verification link to{' '}
                      <span className="font-medium">
                        {email || 'your email address'}
                      </span>
                    </p>
                    <p className="text-gray-600 dark:text-gray-400">
                      Click the link in the email to verify your account. The link will expire in 24 hours.
                    </p>
                    <p className="text-gray-500 dark:text-gray-400">
                      If you don&apos;t see the email, check your spam or junk folder.
                    </p>
                  </div>
                </div>
              </div>
              {import.meta.env.DEV && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
                  <p className="text-sm text-amber-900 dark:text-amber-200">
                    <strong>Development Mode:</strong> If you&apos;re using Firebase emulators, check your
                    terminal for the verification link instead of your email inbox.
                  </p>
                </div>
              )}
              <div className="space-y-3">
                <Button
                  onClick={handleResendVerification}
                  disabled={isResending}
                  className="w-full btn-primary"
                >
                  {isResending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Resend Verification Email'
                  )}
                </Button>
                <Button variant="outline" onClick={handleGoToLogin} className="w-full">
                  Go to Login
                </Button>
              </div>
            </div>
          )}

          {isVerifying && (
            <div className="flex flex-col items-center justify-center rounded-lg border border-border bg-muted/30 py-12 dark:bg-muted/20">
              <Loader2 className="mb-4 h-12 w-12 animate-spin text-primary-600" />
              <p className="text-base font-medium text-gray-900 dark:text-white">Verifying your email…</p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Please wait while we verify your email address.
              </p>
            </div>
          )}
        </div>
      </div>
    </PlatformCheckoutShell>
  );
}
